import { useCallback, useEffect, useRef, useState } from "react";

const PAGINATION_HEIGHT = 64;
const PAGE_PADDING = 16;

/**
 * Measures how much vertical space is actually available below a table's
 * top edge (viewport height minus whatever sits above it) so the table body
 * fills the page instead of using a fixed height that either wastes space
 * on tall screens or clips rows on short ones.
 *
 * Attach `containerRef` to the element wrapping the Table, spread the
 * returned deps you want to recalc on (e.g. loading/data length changes
 * that affect layout, like a filter bar wrapping to a second line).
 */
export default function useTableScrollY(recalcDeps = []) {
  const containerRef = useRef(null);
  const [scrollY, setScrollY] = useState(360);

  const recalc = useCallback(() => {
    if (!containerRef.current) return;
    const { top } = containerRef.current.getBoundingClientRect();
    setScrollY(
      Math.max(200, window.innerHeight - top - PAGINATION_HEIGHT - PAGE_PADDING),
    );
  }, []);

  useEffect(() => {
    recalc();
    window.addEventListener("resize", recalc);
    return () => window.removeEventListener("resize", recalc);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recalc, ...recalcDeps]);

  return [containerRef, scrollY];
}
